import type { TuituiOutboundInteractiveMessage } from '@qihoo/tuitui-bot-sdk';
import { createTuituiClient } from './sdk';
import {
  CHAT_TYPE_CHANNEL,
  CHAT_TYPE_DIRECT,
  CHAT_TYPE_GROUP,
  teamsParseChatId,
  type ChatType,
} from './chat_base';

export async function tuituiEmojiReaction(
  account: any,
  target: string,
  chatType: ChatType,
  msgid: string,
  emoji: string,
): Promise<void> {
  const client = createTuituiClient(account);
  if (chatType === CHAT_TYPE_GROUP) {
    await client.im.emojiReaction({ to: client.to.group(target), messageId: msgid, emoji });
    return;
  }
  if (chatType === CHAT_TYPE_DIRECT) {
    await client.im.emojiReaction({ to: client.to.account(target), messageId: msgid, emoji });
    return;
  }
  const teamsTarget = teamsParseChatId(target);
  await client.teams.emojiReaction({
    teamId: teamsTarget.team_id,
    channelId: teamsTarget.channel_id,
    postId: msgid,
    emoji,
  });
}

interface SendTextMsgOptions {
  channelTagName?: string;
}

export async function sendTextMsg(
  account: any,
  chatId: string | undefined,
  chatType: ChatType,
  content: string,
  options: SendTextMsgOptions = {},
): Promise<void> {
  if (!chatId) throw new Error('[tuitui] target is required');

  const client = createTuituiClient(account);
  if (chatType === CHAT_TYPE_CHANNEL) {
    const target = teamsParseChatId(chatId);
    await client.teams.sendPost({
      teamId: target.team_id,
      channelId: target.channel_id,
      text: content,
      ...(target.parent_id ? { parentId: target.parent_id } : {}),
      ...(options.channelTagName ? { tag: options.channelTagName } : {}),
    });
    return;
  }

  const group = chatType === CHAT_TYPE_GROUP;
  await client.im.sendText({
    to: group ? client.to.group(chatId) : client.to.account(chatId),
    text: content,
  });
}

export async function sendInteractiveMsg(
  account: any,
  chatId: string | undefined,
  chatType: ChatType,
  message: TuituiOutboundInteractiveMessage,
): Promise<{ msgid?: string }> {
  if (!chatId) throw new Error('[tuitui] target is required');
  if (chatType === CHAT_TYPE_CHANNEL) {
    throw new Error('[tuitui] interactive messages are not supported for channels');
  }

  const client = createTuituiClient(account);
  const result = await client.im.sendInteractive({
    to: chatType === CHAT_TYPE_GROUP
      ? client.to.group(chatId)
      : client.to.account(chatId),
    interactive: message,
  });
  return { msgid: result.msgid };
}

export function modifyInteractiveTuituiMsg(
  account: any,
  chatId: string,
  chatType: ChatType,
  msgid: string,
  interactive: TuituiOutboundInteractiveMessage,
) {
  if (chatType === CHAT_TYPE_CHANNEL) {
    throw new Error('[tuitui] interactive message updates are not supported for channels');
  }
  const client = createTuituiClient(account);
  return client.im.modifyInteractive({
    to: chatType === CHAT_TYPE_GROUP
      ? client.to.group(chatId)
      : client.to.account(chatId),
    messageId: msgid,
    interactive,
  });
}

export async function sendMediaMsg(
  account: any,
  chatId: string | undefined,
  chatType: ChatType,
  mediaUrl: string,
  atList?: string[],
): Promise<void> {
  if (!chatId) throw new Error('[tuitui] target is required');

  const client = createTuituiClient(account);
  if (chatType === CHAT_TYPE_CHANNEL) {
    const target = teamsParseChatId(chatId);
    await client.teams.sendFile({
      teamId: target.team_id,
      channelId: target.channel_id,
      ...(target.parent_id ? { parentId: target.parent_id } : {}),
      source: mediaUrl,
      at: atList ?? [],
    });
    return;
  }

  const group = chatType === CHAT_TYPE_GROUP;
  await client.im.sendFile({
    to: group ? client.to.group(chatId) : client.to.account(chatId),
    source: mediaUrl,
    ...(group ? { at: atList ?? [] } : {}),
  });
}
